import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { posts } from "@/data/posts";

const BlogPost = () => {
  const { slug } = useParams();
  const post = posts.find((p) => p.slug === slug);

  usePageMeta(
    post ? `${post.title} | Holistic Healing Psychotherapy, Inc.` : "Blog | Holistic Healing Psychotherapy, Inc.",
    post ? post.excerpt : "Relationship insights from Holistic Healing Psychotherapy, Inc."
  );

  if (!post) return <Navigate to="/blog" replace />;
  const es = post.lang === "es";

  return (
    <div data-testid="blog-post-page">
      <article className="bg-cream-50 pt-14 pb-20 lg:pt-24 lg:pb-28">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <Link
              to="/blog"
              data-testid="blog-back-link"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-sage hover:text-gold-600 transition-colors"
            >
              <ArrowLeft size={14} /> {es ? "Todos los artículos" : "All Articles"}
            </Link>
            <p className="eyebrow mt-10 mb-5" data-testid="blog-post-meta">
              {new Date(post.date + "T12:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })} · {post.readTime}
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-ink" data-testid="blog-post-title">
              {post.title}
            </h1>
            <div className="gold-rule mt-8" />
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="mt-10 space-y-7" data-testid="blog-post-body">
              {post.body.map((block, i) =>
                block.h ? (
                  <div key={i}>
                    <h2 className="font-serif text-2xl sm:text-3xl text-forest-800 mb-3">{block.h}</h2>
                    <p className="text-base sm:text-lg leading-relaxed text-ink/80">{block.p}</p>
                  </div>
                ) : (
                  <p key={i} className="text-base sm:text-lg leading-relaxed text-ink/80">{block.p}</p>
                )
              )}
            </div>
          </FadeIn>

          <FadeIn delay={0.2}>
            <div className="mt-14 rounded-2xl border border-gold-500/35 bg-cream-100 p-8" data-testid="blog-post-cta">
              <p className="font-serif text-2xl text-forest-800 mb-2">{es ? "¿Esto suena como su relación?" : "Does this sound like your relationship?"}</p>
              <p className="text-sm text-sage leading-relaxed mb-6">
                {es
                  ? "Una consulta gratuita de 20 minutos es una forma sencilla de hablar sobre lo que está pasando y si soy la terapeuta adecuada para usted."
                  : "A free 20-minute consultation is a low-pressure way to talk about what's going on and whether I may be the right therapist for you."}
              </p>
              <Link
                to="/contact"
                data-testid="blog-post-schedule-cta"
                className="inline-flex items-center gap-2 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm px-7 py-3.5 transition-colors duration-300 hover:bg-forest-700"
              >
                {es ? "Agende una Consulta" : "Schedule a Consultation"} <ArrowRight size={15} />
              </Link>
            </div>
          </FadeIn>
        </div>
      </article>

      <CTASection
        title={es ? "No tiene que resolver esto solo(a)." : undefined}
        copy={es ? "Agende una consulta gratuita de 20 minutos para ver si somos una buena opción." : undefined}
      />
    </div>
  );
};

export default BlogPost;
